import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AppSidebar } from "@/components/sidebar/app-sidebar";
import { SidebarInset, SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  approveRefund,
  assignTicket,
  downloadAttachment,
  getTicket,
  patchTicket,
  postMessage,
  rejectRefund,
  AdminAttachmentOut,
  AdminMessageOut,
  AdminTicketDetail,
  TicketCategory,
  TicketPriority,
  TicketStatus,
} from "@/services/supportApi";
import {
  refundStatusColor,
  refundStatusLabel,
  ticketCategoryLabel,
  ticketPriorityLabel,
  ticketStatusLabel,
} from "@/lib/supportBadge";
import { resolveTierBadge } from "@/lib/tierBadge";
import { Badge } from "@/components/ui/badge";

const STATUS_OPTIONS: TicketStatus[] = ["open", "pending", "on_hold", "solved", "closed"];
const PRIORITY_OPTIONS: TicketPriority[] = ["low", "normal", "high", "urgent"];
const CATEGORY_OPTIONS: TicketCategory[] = [
  "question",
  "billing",
  "bug",
  "feature_request",
  "account",
  "refund",
  "other",
];

function errMsg(e: unknown, fallback = "Erreur"): string {
  return e instanceof Error ? e.message : fallback;
}

function formatDate(iso?: string | null) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatAmount(cents?: number | null, currency?: string | null) {
  if (cents == null) return "-";
  return `${(cents / 100).toFixed(2)} ${(currency || "eur").toUpperCase()}`;
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
}

const AUTHOR_LABELS: Record<string, string> = {
  customer: "Client",
  agent: "Agent",
  system: "Système",
};

// Affichage en étoiles (lecture seule) de la note de satisfaction (1..5).
function CsatStars({ rating }: { rating: number }) {
  return (
    <span className="text-lg leading-none" aria-label={`Note ${rating} sur 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span key={n} className={n <= rating ? "text-amber-500" : "text-gray-300"}>
          ★
        </span>
      ))}
    </span>
  );
}

function AttachmentLink({
  attachment,
  navigate,
  onError,
}: {
  attachment: AdminAttachmentOut;
  navigate: ReturnType<typeof useNavigate>;
  onError: (msg: string) => void;
}) {
  const [downloading, setDownloading] = useState(false);

  const onClick = async () => {
    setDownloading(true);
    try {
      const url = await downloadAttachment(navigate, attachment.id);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Libère l'object URL après le déclenchement du téléchargement.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e: unknown) {
      onError(errMsg(e, "Erreur de téléchargement"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={downloading}
      className="text-xs text-blue-600 hover:underline disabled:opacity-50"
    >
      {downloading ? "Téléchargement..." : `${attachment.filename} (${formatSize(attachment.size_bytes)})`}
    </button>
  );
}

function MessageBubble({
  message,
  navigate,
  onError,
}: {
  message: AdminMessageOut;
  navigate: ReturnType<typeof useNavigate>;
  onError: (msg: string) => void;
}) {
  // Message système : centré et discret.
  if (message.author_type === "system") {
    return (
      <div className="my-2 text-center text-xs text-gray-500">
        <span>{message.body}</span>
        <span className="ml-2">{formatDate(message.created_at)}</span>
      </div>
    );
  }

  const isAgent = message.author_type === "agent";
  // Note interne : fond ambre + tag explicite "non visible client".
  const internal = message.is_internal;

  return (
    <div className={`flex ${isAgent ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[80%] rounded-lg border p-3 text-sm ${
          internal
            ? "border-amber-300 bg-amber-50"
            : isAgent
              ? "border-blue-200 bg-blue-50"
              : "border-gray-200 bg-gray-50"
        }`}
      >
        <div className="mb-1 flex items-center gap-2 text-xs text-gray-500">
          <span className="font-medium">{AUTHOR_LABELS[message.author_type] ?? message.author_type}</span>
          {message.author_id && <span className="font-mono">{message.author_id.substring(0, 8)}...</span>}
          <span>·</span>
          <span>{formatDate(message.created_at)}</span>
        </div>
        {internal && (
          <div className="mb-1 inline-block rounded bg-amber-200 px-1.5 py-0.5 text-[11px] font-semibold text-amber-900">
            Note interne — non visible client
          </div>
        )}
        <div className="whitespace-pre-wrap text-gray-800">{message.body}</div>
        {message.attachments.length > 0 && (
          <div className="mt-2 flex flex-col gap-1 border-t pt-2">
            {message.attachments.map((a) => (
              <AttachmentLink key={a.id} attachment={a} navigate={navigate} onError={onError} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function SupportDetailPage() {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const ticketId = Number(id);
  const { toast } = useToast();

  const [ticket, setTicket] = useState<AdminTicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Réponse
  const [replyBody, setReplyBody] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [sending, setSending] = useState(false);

  // Drapeaux de chargement par contrôle.
  const [updatingField, setUpdatingField] = useState<string | null>(null);

  // Assignation manuelle
  const [assigneeInput, setAssigneeInput] = useState("");

  // Remboursement
  const [refundAmount, setRefundAmount] = useState("");
  const [approveNote, setApproveNote] = useState("");
  const [rejectNote, setRejectNote] = useState("");
  const [refundAction, setRefundAction] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const t = await getTicket(navigate, ticketId);
      setTicket(t);
    } catch (e: unknown) {
      setError(errMsg(e, "Erreur de chargement"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!ticketId) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  const onSendReply = async () => {
    if (!replyBody.trim()) return;
    setSending(true);
    try {
      const updated = await postMessage(navigate, ticketId, {
        body: replyBody.trim(),
        is_internal: isInternal,
      });
      setTicket(updated);
      setReplyBody("");
      setIsInternal(false);
      toast({ title: isInternal ? "Note interne ajoutée" : "Réponse envoyée" });
    } catch (e: unknown) {
      toast({ title: "Échec", description: errMsg(e), variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const onPatch = async (field: string, patch: Parameters<typeof patchTicket>[2], label: string) => {
    setUpdatingField(field);
    try {
      const updated = await patchTicket(navigate, ticketId, patch);
      setTicket(updated);
      toast({ title: `${label} mis à jour` });
    } catch (e: unknown) {
      toast({ title: "Échec", description: errMsg(e), variant: "destructive" });
    } finally {
      setUpdatingField(null);
    }
  };

  const onSelfAssign = async () => {
    setUpdatingField("assignee");
    try {
      const updated = await assignTicket(navigate, ticketId);
      setTicket(updated);
      toast({ title: "Ticket auto-assigné" });
    } catch (e: unknown) {
      toast({ title: "Échec", description: errMsg(e), variant: "destructive" });
    } finally {
      setUpdatingField(null);
    }
  };

  const onAssignManual = async () => {
    const target = assigneeInput.trim();
    if (!target) return;
    setUpdatingField("assignee");
    try {
      const updated = await assignTicket(navigate, ticketId, target);
      setTicket(updated);
      setAssigneeInput("");
      toast({ title: "Ticket assigné" });
    } catch (e: unknown) {
      toast({ title: "Échec", description: errMsg(e), variant: "destructive" });
    } finally {
      setUpdatingField(null);
    }
  };

  const onApproveRefund = async () => {
    setRefundAction("approve");
    try {
      const cents = refundAmount.trim() ? Math.round(parseFloat(refundAmount) * 100) : undefined;
      await approveRefund(navigate, ticketId, {
        amount_approved_cents: cents != null && !Number.isNaN(cents) ? cents : undefined,
        decision_note: approveNote.trim() || undefined,
      });
      toast({ title: "Remboursement approuvé" });
      setRefundAmount("");
      setApproveNote("");
      await load();
    } catch (e: unknown) {
      toast({ title: "Échec", description: errMsg(e), variant: "destructive" });
    } finally {
      setRefundAction(null);
    }
  };

  const onRejectRefund = async () => {
    if (!rejectNote.trim()) {
      toast({ title: "Motif requis", description: "Indiquez un motif de refus.", variant: "destructive" });
      return;
    }
    setRefundAction("reject");
    try {
      await rejectRefund(navigate, ticketId, { decision_note: rejectNote.trim() });
      toast({ title: "Remboursement refusé" });
      setRejectNote("");
      await load();
    } catch (e: unknown) {
      toast({ title: "Échec", description: errMsg(e), variant: "destructive" });
    } finally {
      setRefundAction(null);
    }
  };

  const refund = ticket?.refund_request ?? null;
  const refundDecided = refund ? refund.status !== "requested" : false;
  const tierBadge = ticket ? resolveTierBadge({ subscription_type: ticket.subscription_type }) : null;

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <Link to="/support" className="text-sm text-gray-600 hover:underline">
              Support
            </Link>
            <span className="text-sm text-gray-400">/</span>
            <h1 className="text-sm font-medium">
              Ticket #{ticketId}
              {ticket ? ` — ${ticket.subject}` : ""}
            </h1>
          </div>
        </header>

        <div className="p-6">
          {error && <div className="mb-4 text-sm text-red-600">{error}</div>}
          {loading && <div className="mb-4 text-sm text-gray-500">Chargement...</div>}

          {ticket && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Colonne principale : fil de discussion + réponse */}
              <div className="space-y-4 lg:col-span-2">
                <section className="rounded border p-4">
                  <h2 className="mb-3 text-lg font-semibold">Conversation</h2>
                  <div className="space-y-3">
                    {ticket.messages.map((m) => (
                      <MessageBubble
                        key={m.id}
                        message={m}
                        navigate={navigate}
                        onError={(msg) =>
                          toast({ title: "Échec", description: msg, variant: "destructive" })
                        }
                      />
                    ))}
                    {!ticket.messages.length && (
                      <div className="text-sm text-gray-500">Aucun message.</div>
                    )}
                  </div>
                </section>

                {/* Boîte de réponse */}
                <section className="rounded border p-4">
                  <div
                    className={`rounded-md p-3 ${
                      isInternal ? "bg-amber-50 ring-1 ring-amber-200" : ""
                    }`}
                  >
                    <Textarea
                      placeholder={
                        isInternal
                          ? "Note interne (non visible par le client)..."
                          : "Votre réponse au client..."
                      }
                      value={replyBody}
                      onChange={(e) => setReplyBody(e.target.value)}
                      rows={4}
                    />
                    <div className="mt-3 flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Switch
                          id="internal-note"
                          checked={isInternal}
                          onCheckedChange={setIsInternal}
                        />
                        <Label htmlFor="internal-note" className="text-sm">
                          Note interne
                          {isInternal && (
                            <span className="ml-2 text-xs font-normal text-amber-700">
                              non visible client
                            </span>
                          )}
                        </Label>
                      </div>
                      <Button onClick={onSendReply} disabled={sending || !replyBody.trim()}>
                        {sending ? "Envoi..." : isInternal ? "Ajouter la note" : "Envoyer"}
                      </Button>
                    </div>
                  </div>
                </section>
              </div>

              {/* Colonne latérale : contrôles + contexte + remboursement + CSAT */}
              <div className="space-y-4">
                {/* Contrôles */}
                <section className="rounded border p-4">
                  <h2 className="mb-3 text-lg font-semibold">Gestion</h2>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs uppercase text-gray-500">Statut</Label>
                      <Select
                        value={ticket.status}
                        onValueChange={(v) =>
                          onPatch("status", { status: v as TicketStatus }, "Statut")
                        }
                        disabled={updatingField === "status"}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUS_OPTIONS.map((s) => (
                            <SelectItem key={s} value={s}>
                              {ticketStatusLabel(s)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs uppercase text-gray-500">Priorité</Label>
                      <Select
                        value={ticket.priority}
                        onValueChange={(v) =>
                          onPatch("priority", { priority: v as TicketPriority }, "Priorité")
                        }
                        disabled={updatingField === "priority"}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {PRIORITY_OPTIONS.map((p) => (
                            <SelectItem key={p} value={p}>
                              {ticketPriorityLabel(p)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs uppercase text-gray-500">Catégorie</Label>
                      <Select
                        value={ticket.category}
                        onValueChange={(v) =>
                          onPatch("category", { category: v as TicketCategory }, "Catégorie")
                        }
                        disabled={updatingField === "category"}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORY_OPTIONS.map((c) => (
                            <SelectItem key={c} value={c}>
                              {ticketCategoryLabel(c)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-xs uppercase text-gray-500">Assignation</Label>
                      <div className="mt-1 text-sm">
                        {ticket.assigned_admin_id ? (
                          <span className="font-mono text-xs">{ticket.assigned_admin_id}</span>
                        ) : (
                          <span className="text-gray-400">Non assigné</span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <Input
                          placeholder="ID admin..."
                          value={assigneeInput}
                          onChange={(e) => setAssigneeInput(e.target.value)}
                          className="h-9"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={onAssignManual}
                          disabled={updatingField === "assignee" || !assigneeInput.trim()}
                        >
                          Assigner
                        </Button>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 px-0 text-blue-600"
                        onClick={onSelfAssign}
                        disabled={updatingField === "assignee"}
                      >
                        M'assigner ce ticket
                      </Button>
                    </div>
                  </div>
                </section>

                {/* Contexte demandeur */}
                <section className="rounded border p-4">
                  <h2 className="mb-3 text-lg font-semibold">Contexte</h2>
                  <dl className="space-y-3 text-sm">
                    <div>
                      <dt className="text-xs uppercase text-gray-500">Demandeur</dt>
                      <dd className="font-mono text-xs">{ticket.requester_user_id}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase text-gray-500">Compte (abonnement)</dt>
                      <dd>
                        {ticket.subscription_user_id ? (
                          <div className="flex flex-col gap-1">
                            <span className="font-mono text-xs">{ticket.subscription_user_id}</span>
                            <div className="flex gap-3 text-xs">
                              <Link
                                to={`/subscriptions/${encodeURIComponent(ticket.subscription_user_id)}`}
                                className="text-blue-600 hover:underline"
                              >
                                Abonnement
                              </Link>
                              <Link
                                to={`/users/${encodeURIComponent(ticket.subscription_user_id)}`}
                                className="text-blue-600 hover:underline"
                              >
                                Utilisateur
                              </Link>
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase text-gray-500">Type d'abonnement</dt>
                      <dd>
                        {tierBadge ? (
                          <Badge variant={tierBadge.variant} className={tierBadge.className}>
                            {tierBadge.label}
                          </Badge>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase text-gray-500">Créé le</dt>
                      <dd>{formatDate(ticket.created_at)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase text-gray-500">Dernier message</dt>
                      <dd>{formatDate(ticket.last_message_at)}</dd>
                    </div>
                  </dl>
                </section>

                {/* Remboursement */}
                {refund && (
                  <section className="rounded border p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h2 className="text-lg font-semibold">Remboursement</h2>
                      <span
                        className={`rounded px-2 py-0.5 text-xs font-medium ${refundStatusColor(
                          refund.status
                        )}`}
                      >
                        {refundStatusLabel(refund.status)}
                      </span>
                    </div>
                    <dl className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Facture Stripe</dt>
                        <dd className="font-mono text-xs">{refund.stripe_invoice_id}</dd>
                      </div>
                      <div className="flex justify-between">
                        <dt className="text-gray-500">Montant demandé</dt>
                        <dd>{formatAmount(refund.amount_requested_cents, refund.currency)}</dd>
                      </div>
                      {refund.amount_approved_cents != null && (
                        <div className="flex justify-between">
                          <dt className="text-gray-500">Montant approuvé</dt>
                          <dd>{formatAmount(refund.amount_approved_cents, refund.currency)}</dd>
                        </div>
                      )}
                      <div>
                        <dt className="text-gray-500">Motif client</dt>
                        <dd className="mt-1 whitespace-pre-wrap text-gray-800">{refund.reason}</dd>
                      </div>
                      {refund.decision_note && (
                        <div>
                          <dt className="text-gray-500">Note de décision</dt>
                          <dd className="mt-1 whitespace-pre-wrap text-gray-800">
                            {refund.decision_note}
                          </dd>
                        </div>
                      )}
                    </dl>

                    {refundDecided ? (
                      <div className="mt-3 text-xs text-gray-500">
                        Décision déjà prise — actions verrouillées.
                      </div>
                    ) : (
                      <div className="mt-4 space-y-3">
                        <div>
                          <Label className="text-xs uppercase text-gray-500">
                            Montant à approuver (optionnel, total par défaut)
                          </Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            placeholder={(refund.amount_requested_cents / 100).toFixed(2)}
                            value={refundAmount}
                            onChange={(e) => setRefundAmount(e.target.value)}
                            className="mt-1 h-9"
                          />
                        </div>
                        <div>
                          <Label className="text-xs uppercase text-gray-500">
                            Note de décision (optionnelle)
                          </Label>
                          <Textarea
                            placeholder="Note interne sur la décision..."
                            value={approveNote}
                            onChange={(e) => setApproveNote(e.target.value)}
                            rows={2}
                            className="mt-1"
                          />
                        </div>

                        <div className="flex gap-2">
                          {/* Approuver */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="sm" disabled={refundAction !== null}>
                                Approuver
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Approuver le remboursement ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Montant :{" "}
                                  {refundAmount.trim()
                                    ? `${parseFloat(refundAmount).toFixed(2)} ${refund.currency.toUpperCase()}`
                                    : formatAmount(refund.amount_requested_cents, refund.currency)}
                                  . Le remboursement sera déclenché côté Stripe. Action
                                  irréversible.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction onClick={onApproveRefund}>
                                  Oui, approuver
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>

                          {/* Refuser */}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm" disabled={refundAction !== null}>
                                Refuser
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Refuser le remboursement ?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Un motif de refus est obligatoire. Il sera enregistré avec la
                                  décision.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <div className="py-2">
                                <Textarea
                                  placeholder="Motif du refus (obligatoire)..."
                                  value={rejectNote}
                                  onChange={(e) => setRejectNote(e.target.value)}
                                  rows={3}
                                />
                              </div>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Annuler</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={(e) => {
                                    // Empêche la fermeture si le motif est vide.
                                    if (!rejectNote.trim()) {
                                      e.preventDefault();
                                    }
                                    void onRejectRefund();
                                  }}
                                >
                                  Confirmer le refus
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    )}
                  </section>
                )}

                {/* Satisfaction (CSAT) */}
                {ticket.satisfaction_rating != null && (
                  <section className="rounded border p-4">
                    <h2 className="mb-3 text-lg font-semibold">Satisfaction</h2>
                    <CsatStars rating={ticket.satisfaction_rating} />
                    {ticket.satisfaction_comment && (
                      <p className="mt-2 whitespace-pre-wrap text-sm text-gray-700">
                        {ticket.satisfaction_comment}
                      </p>
                    )}
                  </section>
                )}
              </div>
            </div>
          )}
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}
